/**
 * Navegação a partir do Assistente da Plataforma.
 * Converte deep links da API (`/team/boards`, `/chat?lead=`) para rotas reais
 * (`/equipe/tarefas`, `/chat/:id`). No cockpit, espelha a busca.
 */

import {
  POPUP_TEAM_BASE,
  TEAM_BASE,
  isPopupEquipePath,
  serviceChatPath,
  teamTasksPaths,
  teamAgendaPaths,
  teamSalesPipelinePaths,
  teamAwarenessPipelinePaths,
  teamPath,
  chatPath,
} from "./teamNavigation";

function openAppPathInNewTab(path) {
  if (typeof window === "undefined" || !path) return;
  const url = `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function splitPathQuery(raw) {
  const s = String(raw || "").trim();
  if (!s) return { pathname: "", search: "" };
  const q = s.indexOf("?");
  if (q < 0) return { pathname: s, search: "" };
  return { pathname: s.slice(0, q), search: s.slice(q) };
}

function leadIdFromChatHref(pathname, search) {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  if (path.startsWith("/chat/")) {
    const rest = path.slice("/chat/".length).split("/")[0];
    return rest || null;
  }
  if (path === "/chat" && search) {
    try {
      const params = new URLSearchParams(String(search).replace(/^\?/, ""));
      return params.get("lead") || params.get("id") || null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Converte href canônico da API/assistente para rota do app principal.
 */
export function mapAssistantHrefToApp(href) {
  const { pathname, search } = splitPathQuery(href);
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  let qs = search || "";

  // Já é rota do app / popup — só normaliza chat?lead=
  if (path === "/chat" || path.startsWith("/chat/")) {
    const leadId = leadIdFromChatHref(path, qs);
    // Remove lead/id da query se viraram path.
    if (leadId && qs) {
      try {
        const params = new URLSearchParams(qs.replace(/^\?/, ""));
        params.delete("lead");
        params.delete("id");
        const rest = params.toString();
        qs = rest ? `?${rest}` : "";
      } catch {
        /* keep */
      }
    }
    return `${chatPath(leadId || undefined)}${qs}`;
  }

  if (path === TEAM_BASE || path.startsWith(`${TEAM_BASE}/`)) {
    return `${path}${qs}`;
  }

  if (isPopupEquipePath(path)) {
    return `${path}${qs}`;
  }

  // Legado API: /team/boards/:id → /equipe/tarefas/:id
  const boardMatch = /^\/team\/boards\/([^/?#]+)/.exec(path);
  if (boardMatch) {
    const tasks = teamTasksPaths(TEAM_BASE);
    return `${tasks.board(boardMatch[1])}${qs}`;
  }
  if (path === "/team/tasks" || path.startsWith("/team/tasks/")) {
    const tasks = teamTasksPaths(TEAM_BASE);
    const rest = path.slice("/team/tasks".length);
    return `${tasks.list}${rest}${qs}`;
  }
  if (path === "/team/agenda" || path.startsWith("/team/agenda/")) {
    return `${teamAgendaPaths(TEAM_BASE).root}${qs}`;
  }
  if (path === "/team" || path.startsWith("/team/")) {
    const suffix = path === "/team" ? "" : path.slice("/team".length);
    return `${TEAM_BASE}${suffix}${qs}`;
  }

  if (path === "/sales-pipeline" || path.startsWith("/sales-pipeline/")) {
    return `${path}${qs}`;
  }

  // Demais rotas (/clients, /settings, …) como estão
  return `${path}${qs}`;
}

/**
 * Converte href canônico da API/assistente para rota do cockpit, se couber.
 * @returns {string|null} path no popup, ou null se deve abrir fora.
 */
export function mapAssistantHrefToPopupEquipe(href) {
  const appPath = mapAssistantHrefToApp(href);
  const { pathname, search } = splitPathQuery(appPath);
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  const qs = search || "";

  if (isPopupEquipePath(path)) {
    return `${path}${qs}`;
  }

  // Atendimento
  if (path === "/chat" || path.startsWith("/chat/")) {
    const leadId = leadIdFromChatHref(path, qs);
    return serviceChatPath(leadId || undefined, POPUP_TEAM_BASE);
  }

  // /equipe/*
  if (path === TEAM_BASE || path.startsWith(`${TEAM_BASE}/`)) {
    const suffix = path === TEAM_BASE ? "" : path.slice(TEAM_BASE.length);
    if (
      suffix.startsWith("/tarefas") ||
      suffix.startsWith("/agenda") ||
      suffix.startsWith("/atendimento") ||
      suffix.startsWith("/sales-pipeline") ||
      suffix === "/pipeline" ||
      suffix.startsWith("/pipeline/")
    ) {
      return `${POPUP_TEAM_BASE}${suffix}${qs}`;
    }
    const spaceId = suffix.replace(/^\//, "") || null;
    return `${teamPath(spaceId || undefined, POPUP_TEAM_BASE)}${qs}`;
  }

  // Pipeline no app principal (sem /equipe)
  if (path === "/sales-pipeline" || path.startsWith("/sales-pipeline/")) {
    const sales = teamSalesPipelinePaths(POPUP_TEAM_BASE);
    const leadPart = path.slice("/sales-pipeline".length).replace(/^\//, "");
    return leadPart ? `${sales.lead(leadPart)}${qs}` : `${sales.root}${qs}`;
  }

  if (path === "/pipeline" || path.startsWith("/pipeline/")) {
    return `${teamAwarenessPipelinePaths(POPUP_TEAM_BASE).root}${qs}`;
  }

  // Clientes, settings, dashboard, etc. → fora do cockpit
  return null;
}

/**
 * Extrai deepLink de respostas variadas do confirm.
 */
export function extractAssistantDeepLink(payload) {
  if (!payload || typeof payload !== "object") return null;
  const candidates = [
    payload.deepLink,
    payload.proposal?.result?.deepLink,
    payload.proposal?.deepLink,
    payload.result?.deepLink,
    payload.data?.deepLink,
    payload.data?.proposal?.result?.deepLink,
  ];
  for (const c of candidates) {
    const s = String(c || "").trim();
    if (s) return s;
  }
  return null;
}

/**
 * @returns {{ mode: 'navigate' | 'external', path: string }}
 */
export function resolveAssistantNavigation(href, { pathname = "" } = {}) {
  const raw = String(href || "").trim();
  if (!raw) return { mode: "external", path: "" };

  if (!isPopupEquipePath(pathname)) {
    return { mode: "navigate", path: mapAssistantHrefToApp(raw) };
  }

  const mapped = mapAssistantHrefToPopupEquipe(raw);
  if (mapped) {
    return { mode: "navigate", path: mapped };
  }

  // Fora do shell do cockpit: abre app canônico em nova aba.
  return { mode: "external", path: mapAssistantHrefToApp(raw) };
}

/**
 * Navega ou abre nova aba conforme o contexto do cockpit.
 * @returns {'navigated' | 'external' | 'noop'}
 */
export function navigateAssistantHref(href, { pathname, navigate, onBeforeNavigate } = {}) {
  const resolved = resolveAssistantNavigation(href, { pathname });
  if (!resolved.path) return "noop";

  if (resolved.mode === "external") {
    onBeforeNavigate?.();
    openAppPathInNewTab(resolved.path);
    return "external";
  }

  onBeforeNavigate?.();
  if (typeof navigate === "function") {
    navigate(resolved.path);
  } else {
    openAppPathInNewTab(resolved.path);
    return "external";
  }
  return "navigated";
}

export { openAppPathInNewTab };
